import React, { PureComponent } from 'react'
import styled from 'styled-components'

import { calendarlist, contact, drive, emailaccounts } from 'gipsy-api'
import { styles, translations } from 'gipsy-misc'
import Calendarline from './calendarline'
import cloneDeep from 'lodash/cloneDeep'
import { Switch, UserPicture } from 'gipsy-ui'
import { Header, SectionContainer } from 'pages/settings/components/common'
import RealTimeClient from 'features/realTimeClient'
import { CALENDARLIST_TOPIC } from 'features/realTimeClient/topics'
import { contactSyncLocalStorageKey, driveOauthLocalStorageKey } from 'features/incrementalCallback'

export const fromSettingsPage = 'fromSettingsPage'

export class EmailAccounts extends PureComponent {
  state = {
    accountMap: {},
    saved: false
  }

  callbackId = CALENDARLIST_TOPIC + '_CalendarList'

  componentDidMount = () => {
    this.getEmailAccounts()
    RealTimeClient.subscribe(CALENDARLIST_TOPIC, () => this.getEmailAccounts(), this.callbackId)
  }

  componentWillUnmount() {
    clearTimeout(this.timeout)
    RealTimeClient.unsubscribe(CALENDARLIST_TOPIC, this.callbackId)
  }

  getEmailAccounts = async () => {
    try {
      const m = await emailaccounts.get()
      this.setState({ accountMap: m })
    } catch (err) {
      this.props.handleAPIError(err)
    }
  }

  onToggleSelect = async (email, calendar) => {
    const selectedCalendar = cloneDeep(calendar)
    this.updateCalendarState(email, calendar)
    let funcToCall = selectedCalendar.isSelected ? calendarlist.unselect : calendarlist.select
    try {
      await funcToCall(email, selectedCalendar.originalCalendarId)
      this.setSave()
    } catch (err) {
      console.error(err)
    }
  }

  onToggleContact = async ({ paramName, value }) => {
    const email = paramName
    this.updateContactSyncState(email, value)

    if (value) {
      try {
        const data = await contact.synchronize(email, true)
        if (!data) {
          this.setSave()
          return
        }

        // if we never asked for access to user contacts, the synchronize endpoint
        // will return a body for an incremental oauth flow to request contacts scope
        if (data.authURL) {
          window.sessionStorage.setItem(`stateOAuth-${data.provider}`, data.state)
          window.sessionStorage.setItem(`nonceOAuth-${data.provider}`, data.nonce)
          window.sessionStorage.setItem(contactSyncLocalStorageKey, fromSettingsPage)
          window.location.href = data.authURL
        }
      } catch (err) {
        this.updateContactSyncState(email, !value)
        this.props.handleAPIError(err)
      }
    } else {
      await contact.unsync(email)
      this.setSave()
    }
  }

  updateCalendarState = (email, calendar) => {
    const calendars = [].concat(this.state.accountMap[email].calendarsList)
    for (let i in calendars) {
      if (calendar.originalCalendarId === calendars[i].originalCalendarId) {
        calendars[i] = {
          ...calendars[i],
          isSelected: !calendars[i].isSelected
        }
        break
      }
    }
    this.setState({
      accountMap: {
        ...this.state.accountMap,
        [email]: {
          ...this.state.accountMap[email],
          calendarsList: calendars
        }
      }
    })
  }

  updateContactSyncState = (email, value) => {
    this.setState({
      accountMap: {
        ...this.state.accountMap,
        [email]: {
          ...this.state.accountMap[email],
          hasSyncedContacts: value
        }
      }
    })
  }

  onToggleDrive = async ({ paramName, value }) => {
    const email = paramName
    this.updateDriveSyncState(email, value)

    if (value) {
      try {
        const data = await drive.activateShareDocs(email)
        if (!data) {
          this.setSave()
          return
        }
        if (data.authURL) {
          window.sessionStorage.setItem(`stateOAuth-${data.provider}`, data.state)
          window.sessionStorage.setItem(`nonceOAuth-${data.provider}`, data.nonce)
          window.sessionStorage.setItem(driveOauthLocalStorageKey, fromSettingsPage)
          window.location.href = data.authURL
        }
      } catch (err) {
        this.updateDriveSyncState(email, !value)
        this.props.handleAPIError(err)
      }
    } else {
      await drive.deactivateShareDocs(email)
      this.setSave()
    }
  }

  updateDriveSyncState = (email, value) => {
    this.setState({
      accountMap: {
        ...this.state.accountMap,
        [email]: {
          ...this.state.accountMap[email],
          shareDrive: value
        }
      }
    })
  }

  setSave = () => {
    this.setState({ saved: true })
    this.timeout = setTimeout(() => {
      this.setState({ saved: false })
    }, 5000)
  }

  renderCalendarListChoices = (email, calendarsList) => {
    return (
      <React.Fragment>
        <AccountSectionHeader>
          <AccountSectionTitle>{translations.settings.section.account.calendars.title}</AccountSectionTitle>
          <AccountSectionSubtitle>{translations.settings.section.account.calendars.subtitle}</AccountSectionSubtitle>
        </AccountSectionHeader>

        {calendarsList.map(calendar => {
          return (
            <Calendarline
              key={calendar.originalCalendarId}
              email={email}
              calendar={calendar}
              onToggleSelect={this.onToggleSelect}
            />
          )
        })}
      </React.Fragment>
    )
  }

  render() {
    const { accountMap } = this.state
    if (!accountMap) {
      return null
    }
    const emails = Object.keys(accountMap)
    return (
      emails && (
        <SectionContainer>
          <Header title={translations.settings.section.account.title} isSaved={this.state.saved} />
          {emails.map(email => {
            const profile = accountMap[email].profile
            const calendarsList = accountMap[email].calendarsList
            const hasSyncedContacts = accountMap[email].hasSyncedContacts
            const shareDrive = accountMap[email].shareDrive
            return (
              <EmailListContainer key={email}>
                <NameContainer>
                  <Picture className="picture">
                    <UserPicture size={32} src={profile && profile.picture} alt="user" />
                  </Picture>
                  <Name>{`${profile.displayName ? `${profile.displayName} - ` : ''} ${email}`}</Name>
                </NameContainer>

                <AccountSectionContainer>
                  {calendarsList && this.renderCalendarListChoices(email, calendarsList)}
                </AccountSectionContainer>

                <AccountSectionContainer>
                  <AccountSectionHeader>
                    <AccountSectionTitle>{translations.settings.section.account.contacts.title}</AccountSectionTitle>
                  </AccountSectionHeader>
                  <SwitchContainer>
                    <Switch paramName={email} value={hasSyncedContacts} onChange={this.onToggleContact} />
                    <span>{translations.settings.section.account.contacts.switchText}</span>
                  </SwitchContainer>
                </AccountSectionContainer>

                {accountMap[email].provider === 'Google' && (
                  <AccountSectionContainer>
                    <AccountSectionHeader>
                      <AccountSectionTitle>{translations.settings.section.account.drive.title}</AccountSectionTitle>
                    </AccountSectionHeader>
                    <SwitchContainer>
                      <Switch paramName={email} value={shareDrive} onChange={this.onToggleDrive} />
                      <span>{translations.settings.section.account.drive.switchText}</span>
                    </SwitchContainer>
                  </AccountSectionContainer>
                )}
              </EmailListContainer>
            )
          })}
        </SectionContainer>
      )
    )
  }
}

const AccountSectionContainer = styled.div`
  margin-bottom: 30px;
`

const NameContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  font-weight: 500;
`

const Name = styled.div`
  font: ${styles.fonts.primaryFont};
  font-size: ${styles.fonts.fontSize};
`

const Picture = styled.div`
  position: relative;
  flex-shrink: 0;
  margin-right: ${styles.spacings.small};
`

const AccountSectionHeader = styled.div`
  color: ${styles.textDarkColor};
  font: ${styles.fonts.primaryFont};
  margin-bottom: 20px;
`

const AccountSectionTitle = styled.div`
  font-size: ${styles.fonts.fontSize};
  font-weight: 500;
  margin-bottom: 3px;
`

const AccountSectionSubtitle = styled.div`
  font-size: ${styles.fonts.fontSizeXSmall};
`

const EmailListContainer = styled.div`
  margin-top: 20px;
`

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  .switch {
    margin-right: 10px;
  }
`

export default EmailAccounts



// WEBPACK FOOTER //
// ./src/pages/settings/components/emailaccounts/index.js